import { useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { Link } from "@tanstack/react-router";
import { clawInstallIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { clearCookieConsent, cookieConsentAtom } from "~/lib/atoms/cookie-consent.ts";

const footerLinks = [
  { to: "/clawinstall", label: "Home" },
  { to: "/clawinstall/pricing", label: "Pricing" },
  { to: "/clawinstall/compare", label: "Compare" },
  { to: "/clawinstall/faq", label: "FAQ" },
  { to: "/clawinstall/security", label: "Security" },
] as const;

/**
 * ClawInstall site footer.
 *
 * Themed via the per-property tokens (light + dark): brand + tagline, nav
 * links, legal (including the "Cookie preferences" reset that re-opens the
 * consent banner), and attribution.
 *
 * @since 1.0.0
 */
export function ClawInstallFooter() {
  const setConsent = useAtomSet(cookieConsentAtom);
  const identity = useAtomValue(clawInstallIdentityAtom);

  // Reset the persisted choice so the consent banner reappears
  const reopenCookieBanner = () => {
    clearCookieConsent();
    setConsent(null);
  };

  return (
    <footer className="border-t border-outline-variant/60 bg-surface-container-low">
      <div className="mx-auto max-w-[1200px] px-6 py-12 md:px-20">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
          {/* Brand */}
          <div>
            <p className="flex items-center gap-2 font-headline-md text-headline-md font-semibold text-on-surface">
              <span className="material-symbols-outlined text-primary-container">cloud_done</span>
              {identity.displayName}
            </p>
            <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
              Professional OpenClaw setup, done right the first time.
            </p>
          </div>

          {/* Links */}
          <div>
            <p className="font-label-md text-label-md text-on-surface">Links</p>
            <ul className="mt-3 space-y-2">
              {footerLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="font-body-md text-body-md text-on-surface-variant transition-colors hover:text-on-surface"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <p className="font-label-md text-label-md text-on-surface">Legal</p>
            <ul className="mt-3 space-y-2">
              <li>
                <Link
                  to="/clawinstall/privacy"
                  className="font-body-md text-body-md text-on-surface-variant transition-colors hover:text-on-surface"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/clawinstall/terms"
                  className="font-body-md text-body-md text-on-surface-variant transition-colors hover:text-on-surface"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/clawinstall/acceptable-use"
                  className="font-body-md text-body-md text-on-surface-variant transition-colors hover:text-on-surface"
                >
                  Acceptable Use
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={reopenCookieBanner}
                  className="font-body-md text-body-md text-on-surface-variant transition-colors hover:text-on-surface"
                >
                  Cookie preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 border-t border-outline-variant/60 pt-8">
          <p className="text-center font-body-md text-body-md text-on-surface-variant">
            &copy; {new Date().getFullYear()} {identity.copyrightHolder}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
